import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Hand, Sparkles } from "lucide-react";
import type { PullOutcome } from "./types";
import { CardBack } from "../fx/CardBack";
import { FlipCard } from "../fx/FlipCard";
import { Confetti } from "../fx/Confetti";
import { RayBurst } from "../fx/Particles";
import { ProductImage } from "../ProductCard";
import { ListingTitle } from "../ListingTitle";
import { rm } from "../../lib/format";
import { sfx } from "../../lib/sfx";
import { EASE, rarityClass, rarityIntensity } from "../../lib/motion";
import { useIsDesktop, usePrefersReducedMotion } from "../../hooks/useBreakpoint";

/**
 * Step 3 — the cards come out of the torn pack face-down. Nothing is spoiled
 * until the collector turns each one over.
 */
export function RevealStage({
  pulls,
  onDone,
}: {
  pulls: PullOutcome[];
  onDone: () => void;
}) {
  const isDesktop = useIsDesktop();
  const reduced = usePrefersReducedMotion();
  const [flipped, setFlipped] = useState<boolean[]>(() => pulls.map(() => false));
  const [active, setActive] = useState(0);
  const [burst, setBurst] = useState<{ key: number; color: string; intensity: 0 | 1 | 2 | 3 } | null>(null);

  useEffect(() => {
    setFlipped(pulls.map(() => false));
    setActive(0);
    setBurst(null);
  }, [pulls]);

  const allFlipped = flipped.every(Boolean);

  const flip = useCallback(
    (index: number) => {
      if (flipped[index]) return;
      const pull = pulls[index];
      if (!pull) return;
      sfx.flip();
      setFlipped((prev) => prev.map((v, i) => (i === index ? true : v)));
      const intensity = rarityIntensity(pull.tierName);
      window.setTimeout(() => {
        sfx.reveal(intensity);
        if (intensity >= 2) setBurst({ key: Date.now(), color: pull.tierColor, intensity });
      }, 340);
    },
    [flipped, pulls],
  );

  const flipAll = useCallback(() => {
    pulls.forEach((_, i) => window.setTimeout(() => flip(i), i * 260));
  }, [flip, pulls]);

  // Auto-advance to the next unflipped card on mobile once one is turned.
  useEffect(() => {
    if (isDesktop || !flipped[active]) return;
    const t = window.setTimeout(() => {
      const next = flipped.findIndex((f, i) => !f && i > active);
      if (next >= 0) setActive(next);
    }, 1900);
    return () => window.clearTimeout(t);
  }, [flipped, active, isDesktop]);

  const best = useMemo(
    () =>
      pulls.reduce<PullOutcome | undefined>(
        (top, p) => (!top || rarityIntensity(p.tierName) > rarityIntensity(top.tierName) ? p : top),
        undefined,
      ),
    [pulls],
  );

  const current = pulls[active];
  if (!current || !best) {
    // Should be unreachable — the ceremony guards against empty pulls before
    // entering this stage — but never render a silent blank screen if it
    // somehow happens; always leave the collector a way out.
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 py-6 text-center">
        <p className="text-sm text-white/60">Something went wrong loading your cards.</p>
        <button
          type="button"
          onClick={onDone}
          className="rounded-xl bg-gradient-brand px-6 py-2.5 text-sm font-bold shadow-lg shadow-brand-pink/30"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-1 flex-col items-center justify-center px-4 py-6">
      <Confetti
        fireKey={burst?.key ?? null}
        intensity={burst?.intensity ?? 2}
        colors={burst ? [burst.color] : undefined}
      />

      <motion.p
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-1 font-display text-[10px] font-bold uppercase tracking-[0.3em] text-brand-pink"
      >
        Step 3 — turn them over
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.35 }}
        className="mb-6 flex items-center gap-1.5 text-[11px] text-white/45"
      >
        <Hand size={12} /> {isDesktop ? "Click a card to flip it" : "Tap the card to flip it"}
      </motion.p>

      {isDesktop ? (
        <div className="flex flex-wrap items-center justify-center gap-5">
          {pulls.map((pull, i) => (
            <motion.div
              key={pull.pullId}
              initial={{ opacity: 0, y: 140, rotate: (i - (pulls.length - 1) / 2) * 8, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
              transition={{ delay: reduced ? 0 : 0.12 * i, duration: 0.7, ease: EASE.outExpo }}
            >
              <PullCard pull={pull} flipped={!!flipped[i]} onFlip={() => flip(i)} size="lg" />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="relative flex w-full flex-col items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 120, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -60, scale: 0.9 }}
              transition={{ duration: 0.55, ease: EASE.outExpo }}
            >
              <PullCard
                pull={current}
                flipped={!!flipped[active]}
                onFlip={() => flip(active)}
                size="md"
                counter={`${active + 1} / ${pulls.length}`}
              />
            </motion.div>
          </AnimatePresence>

          {pulls.length > 1 && (
            <div className="mt-5 flex items-center gap-1.5">
              {pulls.map((p, i) => (
                <button
                  key={p.pullId}
                  type="button"
                  aria-label={`Card ${i + 1}`}
                  onClick={() => {
                    sfx.tap();
                    setActive(i);
                  }}
                  className={`h-1.5 rounded-full transition-all ${
                    i === active ? "w-6 bg-brand-pink" : flipped[i] ? "w-1.5 bg-white/60" : "w-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex flex-col items-center gap-2.5">
        {allFlipped ? (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            type="button"
            onClick={onDone}
            className="rounded-xl bg-gradient-brand px-7 py-3 text-sm font-bold shadow-lg shadow-brand-pink/30 transition-transform active:scale-95"
          >
            See your haul
          </motion.button>
        ) : (
          pulls.length > 1 && (
            <button
              type="button"
              onClick={flipAll}
              className="inline-flex items-center gap-1.5 rounded-xl border border-brand-pink/40 px-5 py-2.5 text-xs font-semibold text-brand-pink transition-colors hover:bg-brand-pink/10"
            >
              <Sparkles size={13} /> Reveal all
            </button>
          )
        )}
        {allFlipped && (
          <p className="text-[10px] text-white/35">
            Best hit: <span style={{ color: best.tierColor }}>{best.tierName}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function PullCard({
  pull,
  flipped,
  onFlip,
  size,
  counter,
}: {
  pull: PullOutcome;
  flipped: boolean;
  onFlip: () => void;
  size: "md" | "lg";
  counter?: string;
}) {
  const intensity = rarityIntensity(pull.tierName);
  const dims = size === "lg" ? "h-[340px] w-[243px]" : "h-[326px] w-[233px]";

  return (
    // `isolate` keeps RayBurst's negative z-index inside this card instead of
    // sinking it behind the ceremony backdrop.
    <div className="relative isolate">
      <RayBurst active={flipped} intensity={intensity} />

      <FlipCard
        flipped={flipped}
        onClick={onFlip}
        disabled={flipped}
        ariaLabel={flipped ? pull.productName : "Flip this card over"}
        className={dims}
        back={
          <div className="h-full w-full">
            <CardBack
              className="h-full w-full shadow-2xl shadow-black/60"
              imageUrl={pull.categoryBackingImageUrl}
              label={counter}
            />
          </div>
        }
        front={
          <div
            className={`relative h-full w-full overflow-hidden rounded-2xl bg-surface ${rarityClass(pull.tierName)} ${
              flipped ? "rarity-aura rarity-aura-pulse" : ""
            }`}
            style={{ ["--rarity-color" as string]: pull.tierColor }}
          >
            <ProductImage src={pull.productImage} alt={pull.productName} className="h-full w-full" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/92 via-black/50 to-black/15" />
            <div className="absolute inset-x-0 top-0 z-10 flex justify-center p-3">
              <span
                className="rounded-full border px-2.5 py-1 font-display text-[9px] font-extrabold uppercase tracking-[0.2em] backdrop-blur-sm"
                style={{
                  color: pull.tierColor,
                  borderColor: `${pull.tierColor}88`,
                  background: `${pull.tierColor}22`,
                }}
              >
                {pull.tierName}
              </span>
            </div>
            <div className="absolute inset-x-0 bottom-0 z-10 p-3 text-center drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
              <ListingTitle className="font-semibold text-white">{pull.productName}</ListingTitle>
              {pull.estValue != null && (
                <p className="mt-1 font-display text-[11px] font-bold text-white/85">est. {rm(pull.estValue)}</p>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
}
